import React, { useState, useEffect } from 'react';
import { Zap, Plus, Minus, User, Calendar, Receipt, ChevronRight, Loader2, Settings } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardCard from '@/components/ui/DashboardCard';
import GlassIcon from '@/components/ui/GlassIcon';
import GlassPill from '@/components/ui/GlassPill';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { SubscriptionPlan } from '@/types/subscription-types';
import { useSubscription } from '@/hooks/subscription/useSubscription';
import { cn } from '@/lib/utils';

interface CreditTransaction {
  id: string;
  credits_amount: number;
  transaction_type: string;
  reason: string;
  metadata: any;
  stripe_charge_id: string | null;
  stripe_invoice_id: string | null;
  admin_user_id: string | null;
  created_at: string;
}

interface CreditTransactionsCardProps {
  currentPlan?: SubscriptionPlan;
  onManageSubscription?: () => void;
}

const CreditTransactionsCard: React.FC<CreditTransactionsCardProps> = ({ currentPlan, onManageSubscription }) => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { status: subscriptionStatus } = useSubscription();
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.id) {
      // Reset pagination state and fetch first page
      setPage(0);
      setHasMore(true);
      setTransactions([]);
      fetchTransactions(0, false);
    }
  }, [user?.id]);

  const fetchTransactions = async (pageNum = 0, append = false) => {
    try {
      if (pageNum === 0) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }

      const pageSize = 10;
      const { data, error } = await supabase
        .from('ai_credit_transactions')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

      if (error) throw error;

      const newTransactions = data || [];
      
      if (append) {
        setTransactions(prev => [...prev, ...newTransactions]);
      } else {
        setTransactions(newTransactions);
      }

      // Check if we have more data
      setHasMore(newTransactions.length === pageSize);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      if (pageNum === 0) {
        setError('Failed to load deposit history');
      } else {
        toast({
          title: "Error",
          description: "Failed to load more transactions",
          variant: "error",
        });
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreTransactions = async () => {
    if (!hasMore || isLoadingMore) return;
    await fetchTransactions(page + 1, true);
  };

  // Set up scroll event listener for infinite scroll
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const handleScroll = () => {
      const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (!viewport) return;

      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100; // 100px threshold
      
      if (isNearBottom && hasMore && !isLoadingMore) {
        loadMoreTransactions();
      }
    };

    const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) {
      viewport.addEventListener('scroll', handleScroll);
      return () => viewport.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoadingMore, loadMoreTransactions]);

  const getTransactionIcon = (type: string, amount: number) => {
    if (amount > 0) {
      return <Plus className="h-4 w-4 text-blue-500" />;
    } else {
      return <Minus className="h-4 w-4 text-red-500" />;
    }
  };

  const getTransactionTypeDisplay = (type: string, reason: string) => {
    const typeMap: { [key: string]: { label: string; variant: any } } = {
      subscription: { label: 'Subscription', variant: 'blue' },
      purchase: { label: 'Purchase', variant: 'blue' },
      admin_credit: { label: 'Admin Credit', variant: 'blue' },
      refund: { label: 'Refund', variant: 'amber' },
      deduction: { label: 'Deduction', variant: 'red' },
    };

    return typeMap[type] || { label: reason || type, variant: 'default' };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };


  if (isLoading) {
    return (
      <DashboardCard className="h-full flex flex-col group">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            Credit Deposits
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading deposits...</span>
          </div>
        </div>
      </DashboardCard>
    );
  }

  if (error) {
    return (
      <DashboardCard className="h-full flex flex-col group">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            Credit Deposits
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 text-sm mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={() => fetchTransactions(0, false)}>
              Retry
            </Button>
          </div>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard className="h-full flex flex-col group">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            Credit Deposits
          </h3>
        </div>
        {transactions.length > 0 && (
          <GlassPill 
            type={`${transactions.length}${hasMore ? '+' : ''} recent`} 
            variant="blue" 
            className="text-xs hidden sm:block"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {transactions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg mx-auto">
                <Receipt className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No deposits yet</p>
                <p className="text-xs text-muted-foreground mt-1">Your credit deposit history will appear here</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <ScrollArea ref={scrollAreaRef} className="h-full" orientation="vertical">
              <div className="space-y-3 px-0 py-2">
                {transactions.map((transaction) => {
                  const typeDisplay = getTransactionTypeDisplay(transaction.transaction_type, transaction.reason);
                  const isPositive = transaction.credits_amount > 0;
                  
                  return (
                    <div 
                      key={transaction.id}
                      className="group relative rounded-lg p-3 transition-all duration-200 hover:bg-white/30 dark:hover:bg-black/20"
                    >
                      {/* Glass background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-white/10 dark:from-black/20 dark:to-black/10 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      
                      <div className="relative flex items-center gap-3">
                        {/* Transaction icon */}
                        <div className="flex-shrink-0 hidden sm:block">
                          {getTransactionIcon(transaction.transaction_type, transaction.credits_amount)}
                        </div>
                        
                        {/* Transaction type badge */}
                        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                          <GlassPill 
                            type={typeDisplay.label} 
                            variant={typeDisplay.variant}
                            className="text-xs"
                          />
                          {transaction.admin_user_id && (
                            <div title="Admin action">
                              <User className="h-3 w-3 text-blue-500" />
                            </div>
                          )}
                        </div>
                        
                        {/* Description */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                            {transaction.reason}
                          </p>
                        </div>
                        
                        {/* Date */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatDate(transaction.created_at)}
                          </span>
                        </div>
                        
                        {/* Credit amount */}
                        <div className="text-right flex-shrink-0">
                          <div className={cn(
                            "text-sm font-semibold",
                            isPositive ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'
                          )}>
                            {isPositive ? '+' : ''}{transaction.credits_amount.toLocaleString()} Credits
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  );
                })}
                
                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading more deposits...</span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
        
        {/* Bottom section - pinned to bottom */}
        <div className="mt-auto">
          {transactions.length > 0 && subscriptionStatus?.customer?.id && (
            <div className="pt-4 mt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <motion.button
                onClick={() => {
                  // Use direct portal route to bypass loading states
                  window.location.href = '/billing?portal=true';
                }}
                className="w-full p-4 rounded-lg font-semibold text-white transition-all duration-200
                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                           hover:shadow-lg bg-gradient-to-r from-blue-500 to-blue-600 
                           hover:from-blue-600 hover:to-blue-700 hover:shadow-blue-500/25"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.995 }}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Receipt className="w-5 h-5" />
                  <span>View Invoices</span>
                </div>
              </motion.button>
            </div>
          )}
          {transactions.length > 0 && !subscriptionStatus?.customer?.id && (
            <div className="pt-4 mt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <div className="text-center">
                <div className="w-full p-4 rounded-lg font-semibold transition-all duration-200
                               bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Receipt className="w-5 h-5" />
                    <span>View Invoices</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardCard>
  );
};

export default CreditTransactionsCard; 